import Link from 'next/link'
import { useRouter } from 'next/router'
import * as React from 'react'

import { cn } from '@/lib/utils'
import useLocale from '@/hooks/use-locale'

import { CourseGeneral, DiscountEnum } from '@/data/course-general'

import { PageActions } from '@/components/common/page-header'
import SectionHeader, { SectionPropsHeader } from '@/components/common/section-header'
import { Shell } from '@/components/common/shell'
import { Icons } from '@/components/icons'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'

import { FORM_REGISTER_URL } from '@/constant/app'

interface ModuleInformationProps {
  className?: string
  children: React.ReactNode
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  course?: Record<string, any>
  isCourseEnrolled: boolean
}

export function ModuleInformationSection({ className, children, course, isCourseEnrolled }: ModuleInformationProps) {
  const { text, lang } = useLocale()
  const router = useRouter()
  const courseSlug = router.asPath.split('/')[3].split('?')[0]

  return (
    <Shell className={cn('mt-16 sm:mt-20 md:mt-24', className)}>
      {children}
      <PageActions className="justify-center flex-col sm:flex-row gap-4 mt-6 sm:mt-8 md:mt-10 space-x-0 sm:space-x-4">
        <Button size="lg" asChild className="w-full sm:w-auto">
          {lang === 'vi' ? (
            <Link href={FORM_REGISTER_URL} target="_blank" rel="noopener noreferrer">
              {text.MAIN.INTRODUCTION.HEADING_4}
            </Link>
          ) : (
            <Link
              href={
                isCourseEnrolled
                  ? '/my-courses'
                  : courseSlug === 'mlops-1'
                    ? `/checkout?course=${courseSlug}`
                    : router.asPath
              }
              rel="noopener noreferrer"
            >
              {isCourseEnrolled
                ? text.MAIN.INTRODUCTION.HEADING_6
                : courseSlug === 'mlops-1'
                  ? text.MAIN.INTRODUCTION.HEADING_5
                  : text.MAIN.INTRODUCTION.HEADING_7}
            </Link>
          )}
        </Button>
        {lang === 'en' && (
          <Button asChild size="lg" variant="outline" className="w-full sm:w-auto">
            <Link href={course?.detailCourseEngUrl || '#'} target="_blank" rel="noopener noreferrer">
              {text.MAIN.BODY.COURSE.DETAIL_COURSE}
            </Link>
          </Button>
        )}
      </PageActions>
    </Shell>
  )
}

type ModuleInformationHeadingProps = SectionPropsHeader

export function ModuleInformationHeading(props: ModuleInformationHeadingProps) {
  return <SectionHeader {...props} />
}

export function GroupModuleInformation({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={cn('my-8 grid gap-6 sm:gap-8 grid-cols-1 lg:grid-cols-3 lg:gap-10', className)}>{children}</div>
  )
}

export function CourseModuleInformationContent({
  className,
  courseInformation,
}: {
  className?: string
  courseInformation: {
    finalProject: number
    schedule: string
    duration: string
    durationProject: string
    accessDuration: string
    labNumber: number
  }
}) {
  const { text, lang } = useLocale()

  return (
    <Card
      className={cn(
        'overflow-hidden rounded-2xl flex flex-col justify-between h-full relative hover:shadow-2xl transition-all ease-in hover:shadow-primary',
        className
      )}
    >
      <Icons.primaryIcon className="absolute top-5 right-5 z-10 size-8" />
      <div className="mb-4">
        <CardHeader className="relative flex flex-col justify-end p-6">
          <div className="space-y-4 z-10">
            <h3 className="text-4xl font-semibold">{text.COURSE_MODULE.INFORMATION.TITLE}</h3>
          </div>
        </CardHeader>
        <CardContent className="py-6 space-y-4">
          <div className="text-base flex space-x-2 items-end">
            <p>📌</p>
            <p className="text-sm">
              <b>{text.COURSE_MODULE.INFORMATION.FINAL_PROJECT}: </b>
              <span>{courseInformation.finalProject}</span>
            </p>
          </div>
          <div className="text-base flex space-x-2 items-end">
            <p>📚</p>
            <p className="text-sm">
              <b>{text.COURSE_MODULE.INFORMATION.LAB_NUMBER}: </b>
              <span>{courseInformation.labNumber}</span>
            </p>
          </div>
          {lang === 'vi' && courseInformation.schedule && (
            <>
              <Separator className="w-full" />
              <div className="text-base flex space-x-2 items-end">
                <p>🗓️</p>
                <p className="text-sm">
                  <b>{text.COURSE_MODULE.INFORMATION.SCHEDULE}: </b>
                  <span>{courseInformation.schedule}</span>
                </p>
              </div>
            </>
          )}
          {courseInformation.duration && (
            <div className="text-base flex space-x-2 items-end">
              <p>⏰</p>
              <p className="text-sm">
                <b>{text.COURSE_MODULE.INFORMATION.TOTAL_DURATION}: </b>
                <span>{courseInformation.duration}</span>
              </p>
            </div>
          )}
          {courseInformation.durationProject && (
            <>
              <Separator className="w-full" />
              <div className="text-base flex space-x-2 items-end">
                <p>📕</p>
                <p className="text-sm">
                  <b>{text.COURSE_MODULE.INFORMATION.PROJECT_DURATION}: </b>
                  <span>{courseInformation.durationProject}</span>
                </p>
              </div>
            </>
          )}
          {courseInformation.accessDuration && (
            <>
              <div className="text-base flex space-x-2 items-end">
                <p>📊</p>
                <p className="text-sm">
                  <b>{text.COURSE_MODULE.INFORMATION.ACCESS_DURATION}: </b>
                  <span>{courseInformation.accessDuration}</span>
                </p>
              </div>
            </>
          )}
        </CardContent>
      </div>
    </Card>
  )
}

export function LeadingFormat({ className, type }: { className?: string; type?: string }) {
  const { text, lang } = useLocale()

  return (
    <Card
      className={cn(
        'overflow-hidden rounded-2xl flex flex-col h-full relative hover:shadow-2xl transition-all ease-in hover:shadow-primary',
        className
      )}
    >
      <CardHeader className="relative flex flex-col justify-end p-6">
        <div className="space-y-4 z-10">
          <h3 className="text-4xl font-semibold">{text.COURSE_MODULE.FORMAT.TITLE}</h3>
        </div>
      </CardHeader>
      <CardContent>
        <div className="p-4 space-y-4">
          <div className="space-y-3">
            <h3 className="font-semibold flex items-center gap-2">
              <span className="text-lg">💻</span>
              <span className="text-base">{text.COURSE_MODULE.FORMAT.LEARNING_FORMAT.TITLE}</span>
            </h3>
            <ul className="space-y-2 text-sm text-accent-foreground">
              <li className="flex items-start gap-2">
                <span className="mt-1">•</span>
                {lang === 'vi' && type === 'live'
                  ? text.COURSE_MODULE.FORMAT.LEARNING_FORMAT.ONLINE
                  : text.COURSE_MODULE.FORMAT.LEARNING_FORMAT.PRE_RECORDED}
              </li>
              <li className="flex items-end gap-2">
                <span className="mt-1">•</span>
                {text.COURSE_MODULE.FORMAT.LEARNING_FORMAT.SUPPORT}
              </li>
            </ul>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

export function DiscountModule({
  className,
  course,
}: {
  className?: string
  courseModule: CourseGeneral
  course?: string
}) {
  const courseDiscount: Record<DiscountEnum, React.ReactNode> = {
    [DiscountEnum.mlops]: (
      <div className="">
        <h3 className="font-semibold text-lg flex items-center gap-2">
          <span className="text-primary">📊</span>
          <span>MLOps</span>
        </h3>
        <ul className="text-sm text-accent-foreground pl-1">
          <li className="flex items-center gap-3 bg-accent/5 p-2 rounded-lg hover:bg-accent/10 transition-colors">
            <span className="text-primary">🎁</span>
            <span>
              1 module: <span className="font-medium text-primary">Giảm 10%</span>
            </span>
          </li>
          <li className="flex items-center gap-3 bg-accent/5 p-2 rounded-lg hover:bg-accent/10 transition-colors">
            <span className="text-primary">🎁</span>
            <span>
              2 modules: <span className="font-medium text-primary">Giảm 15%</span>
            </span>
          </li>
          <li className="flex items-center gap-3 bg-accent/5 p-2 rounded-lg hover:bg-accent/10 transition-colors">
            <span className="text-primary">🎁</span>
            <span>
              3 modules: <span className="font-medium text-primary">Giảm 25%</span>
            </span>
          </li>
          <li className="flex items-center gap-3 bg-accent/5 p-2 rounded-lg hover:bg-accent/10 transition-colors">
            <span className="text-yellow-500">⚠️</span>
            <span>
              Module 4 không được tặng kèm, nếu muốn nhận module 4 thì chương trình giảm giá{' '}
              <span className="font-medium text-primary">25%</span> sẽ không áp dụng
            </span>
          </li>
        </ul>
      </div>
    ),
    igds: (
      <div className="space-y-4">
        <h3 className="font-semibold text-lg flex items-center gap-2">
          <span className="text-primary">📈</span>
          <span>IGDS</span>
        </h3>
        <ul className="text-sm text-accent-foreground pl-1">
          <li className="flex items-center gap-3 bg-accent/5 p-2 rounded-lg hover:bg-accent/10 transition-colors">
            <span className="text-primary">🎁</span>
            <span>
              1 module: <span className="font-medium text-primary">Giảm 10%</span>
            </span>
          </li>
          <li className="flex items-center gap-3 bg-accent/5 p-2 rounded-lg hover:bg-accent/10 transition-colors">
            <span className="text-primary">🎁</span>
            <span>
              2 modules: <span className="font-medium text-primary">Giảm 25%</span>
            </span>
          </li>
          <li className="flex flex-col gap-3 bg-accent/5 p-2 rounded-lg hover:bg-accent/10 transition-colors">
            <div className="flex items-center gap-3">
              <span className="text-primary">✨</span>
              <p className="font-medium">Đăng ký thêm:</p>
            </div>
            <ul className="pl-8 space-y-2">
              <li className="flex items-center gap-2">
                <span className="text-primary">🎁</span>
                <span>
                  1 module: <span className="font-medium text-primary">Giảm 30%</span>
                </span>
              </li>
              <li className="flex items-center gap-2">
                <span className="text-primary">🎁</span>
                <span>
                  2 modules: <span className="font-medium text-primary">Giảm 35%</span>
                </span>
              </li>
              <li className="flex items-center gap-2">
                <span className="text-primary">🎁</span>
                <span>
                  3 modules: <span className="font-medium text-primary">Giảm 40%</span>
                </span>
              </li>
              <li className="flex items-center gap-2">
                <span className="text-primary">🎉</span>
                <span>
                  4 modules: <span className="font-medium text-primary">Giảm 50%</span> + tặng các module còn lại
                </span>
              </li>
            </ul>
          </li>
        </ul>
      </div>
    ),
    cda: (
      <div className="space-y-4">
        <h3 className="font-semibold text-lg flex items-center gap-2">
          <span className="text-primary">📉</span>
          <span>CDA</span>
        </h3>
        <ul className="space-y-3 text-sm text-accent-foreground pl-1">
          <li className="flex items-center gap-3 bg-accent/5 p-2 rounded-lg hover:bg-accent/10 transition-colors">
            <span className="text-primary">🎁</span>
            <span>
              1 module: <span className="font-medium text-primary">Giảm 10%</span>
            </span>
          </li>
          <li className="flex items-center gap-3 bg-accent/5 p-2 rounded-lg hover:bg-accent/10 transition-colors">
            <span className="text-primary">🎁</span>
            <span>
              2 modules: <span className="font-medium text-primary">Giảm 25%</span>
            </span>
          </li>
        </ul>
      </div>
    ),
    aide: <></>,
    dsaa: <></>,
  }

  const key = course as DiscountEnum
  return (
    <Card
      className={cn(
        'overflow-hidden rounded-2xl flex flex-col h-full relative hover:shadow-2xl transition-all ease-in hover:shadow-primary',
        className
      )}
    >
      <CardHeader className="relative flex flex-col justify-end p-6">
        <div className="space-y-4 z-10">
          <h3 className="text-4xl font-semibold">Discount</h3>
        </div>
      </CardHeader>
      <CardContent>
        <div className="p-4 space-y-4">{courseDiscount[key]}</div>
      </CardContent>
    </Card>
  )
}
